import React, { useState, useEffect, useContext } from 'react';
import { IoClose } from 'react-icons/io5';
import { AppContext } from '../../context/appContext';
import { getWhisperMusic } from '../api/whisperMusicApi';
import { getVideos } from '../api/videoApi';
import { getAudioCategoriesDropdown } from '../api/audioCategoryApi';
import { getVideoCategoriesDropdown } from '../api/videoCategoryApi';
import { getCategoriesDropdown as getMiscCategoriesDropdown } from '../api/categoryApi';
import { getAllContent as getMiscContent } from '../api/contentApi';

/**
 * ContentPickerModal - Browse and select content (audio/video)
 * @param {string} type - 'audio' | 'video' | 'misc'
 * @param {boolean} isOpen - Modal open state
 * @param {function} onClose - Close modal callback
 * @param {function} onSelect - Selection callback (contentId, contentTitle)
 */
const ContentPickerModal = ({ type, isOpen, onClose, onSelect }) => {
  const { t, isRTL } = useContext(AppContext);

  const [content, setContent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Fetch categories for filter
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen, type]);

  // Fetch content only when a category is selected
  useEffect(() => {
    if (isOpen && categoryFilter) {
      fetchContent();
    } else {
      setContent([]);
    }
  }, [isOpen, type, search, categoryFilter, currentPage]);

  const fetchCategories = async () => {
    try {
      let response;
      if (type === 'audio') {
        response = await getAudioCategoriesDropdown();
      } else if (type === 'video') {
        response = await getVideoCategoriesDropdown();
      } else if (type === 'misc') {
        response = await getMiscCategoriesDropdown();
      }

      if (response?.data?.data && Array.isArray(response.data.data)) {
        setCategories(response.data.data);
      } else if (response?.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchContent = async () => {
    setLoading(true);
    try {
      let response;
      const options = {
        currentPage,
        limit: itemsPerPage,
        categoryId: categoryFilter || undefined
      };

      if (type === 'audio') {
        response = await getWhisperMusic(search, options);
      } else if (type === 'video') {
        response = await getVideos(currentPage, itemsPerPage, search, categoryFilter || undefined);
      } else if (type === 'misc') {
        response = await getMiscContent(currentPage, itemsPerPage, search, categoryFilter || undefined);
      }

      if (response?.data?.data && Array.isArray(response.data.data)) {
        setContent(response.data.data);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
        }
      } else if (response?.data && Array.isArray(response.data)) {
        setContent(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
        }
      } else {
        setContent([]);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setContent([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item) => {
    onSelect({
      contentId: item._id,
      contentTitle: item.title
    });
    onClose();
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
    setCurrentPage(1);
  };

  const getContentTypeLabel = () => {
    switch (type) {
      case 'audio':
        return t('common.audioContent') || 'Audio Content';
      case 'video':
        return t('common.videoContent') || 'Video Content';
      case 'misc':
        return t('common.miscContent') || 'More (بیشتر) Item';
      default:
        return t('common.content') || 'Content';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">
            {t('common.select') || 'Select'} {getContentTypeLabel()}
          </h3>
          <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost">
            <IoClose size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder={t('common.search') || 'Search...'}
            className="input input-bordered w-full"
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        {/* Category Selection (Required) */}
        {categories.length > 0 && (
          <div className="mb-4">
            <label className="label">
              <span className="label-text font-medium">
                {t('common.selectCategory') || 'Select Category'} <span className="text-red-500">*</span>
              </span>
            </label>
            <select
              className="select select-bordered w-full"
              value={categoryFilter}
              onChange={handleCategoryChange}
            >
              <option value="">{t('common.chooseCategoryFirst') || '-- Choose a Category First --'}</option>
              {categories.map(cat => (
                <option key={cat._id} value={cat._id}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Content List */}
        <div className="overflow-y-auto max-h-96">
          {!categoryFilter ? (
            <div className="text-center py-8 text-gray-400">
              <svg className="mx-auto h-12 w-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              <p>{t('common.selectCategoryFirstToView') || 'Please select a category first to view content'}</p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-md"></span>
            </div>
          ) : content.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('common.noContentInCategory') || 'No content found in this category'}
            </div>
          ) : (
            <div className="space-y-2">
              {content.map(item => (
                <div
                  key={item._id}
                  onClick={() => handleSelect(item)}
                  className="p-3 border rounded-lg hover:bg-base-200 cursor-pointer transition-colors"
                >
                  <div className="font-medium">{item.title}</div>
                  {item.description && (
                    <div className="text-sm text-gray-500 mt-1">
                      {item.description.substring(0, 100)}
                      {item.description.length > 100 && '...'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              {t('common.previous') || 'Previous'}
            </button>
            <span className="py-2 px-3">
              {currentPage} / {totalPages}
            </span>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              {t('common.next') || 'Next'}
            </button>
          </div>
        )}

        {/* Cancel Button */}
        <div className="modal-action">
          <button onClick={onClose} className="btn">
            {t('common.cancel') || 'Cancel'}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
};

export default ContentPickerModal;
